import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FADE_UP, REVEAL_VIEW } from "@/lib/motionPresets";
import { ArrowRight, Check, FileText, ShieldCheck, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/apiClient";
import { trackLead } from "@/lib/tracking";
import { log } from "@/lib/log";
import { useLang } from "@/lib/siteI18n";

const GOLD = "#C9A84C";
const STRIPE_LINK = "https://buy.stripe.com/6oU4gz4lagPQ5MU67Z0sU0h";

const Reveal = ({ children, delay = 0 }) => (
  <motion.div {...REVEAL_VIEW} transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}>{children}</motion.div>
);

const StripeButton = ({ label, testId }) => (
  <a
    href={STRIPE_LINK}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className="inline-flex items-center justify-center gap-3 px-10 py-5 text-xs uppercase tracking-[0.2em] font-semibold text-brand-ink transition-transform hover:scale-[1.02]"
    style={{ backgroundColor: GOLD }}
  >
    {label} <ArrowRight size={16} />
  </a>
);

const SectionTitle = ({ kicker, children }) => (
  <div className="mb-10">
    {kicker && <p className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: GOLD }}>{kicker}</p>}
    <h2 className="font-serif text-3xl md:text-4xl leading-tight text-white">{children}</h2>
  </div>
);

function LeadMagnetForm() {
  const { t } = useLang();
  const a = t.analyse;
  const [form, setForm] = useState({ prenom: "", email: "", birthdate: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.prenom.trim() || !form.email.trim() || !form.birthdate) {
      setError(a.leadErr);
      return;
    }
    setError("");
    setStatus("loading");
    try {
      const [y, m, d] = form.birthdate.split("-");
      const { data } = await api.post("/leads/capture", {
        full_name: form.prenom.trim(),
        prenoms: form.prenom.trim(),
        email: form.email.trim().toLowerCase(),
        birthdate: `${d}/${m}/${y}`,
        product: "chemin_de_vie_rapport",
      });
      trackLead({ content_name: "chemin_de_vie_rapport", eventID: data?.capi_event_id || undefined });
      setStatus("done");
    } catch (err) {
      log.error("[rapport] opt-in échoué :", err);
      setError(a.leadErrSend);
      setStatus("idle");
    }
  };

  if (status === "done") {
    return (
      <div className="border p-8 text-center" style={{ borderColor: `${GOLD}66` }} data-testid="leadmagnet-success">
        <Check className="mx-auto mb-4" size={28} style={{ color: GOLD }} />
        <p className="font-serif text-2xl text-white mb-2">{a.leadDone}</p>
        <p className="text-white/60 text-sm">{a.leadDoneSub}</p>
      </div>
    );
  }

  const inputCls = "w-full bg-transparent border border-white/20 focus:border-[#C9A84C] outline-none px-4 py-3 text-white placeholder-white/30 text-sm transition-colors";
  return (
    <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-3" data-testid="leadmagnet-form" noValidate>
      <input className={inputCls} placeholder={a.leadPrenom} value={form.prenom}
        onChange={(e) => setForm({ ...form, prenom: e.target.value })} data-testid="leadmagnet-prenom" />
      <input className={inputCls} type="email" placeholder={a.leadEmail} value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="leadmagnet-email" />
      <input className={`${inputCls} [color-scheme:dark]`} type="date" value={form.birthdate} aria-label={a.leadDate}
        onChange={(e) => setForm({ ...form, birthdate: e.target.value })} data-testid="leadmagnet-birthdate" />
      {error && <p className="md:col-span-3 text-sm text-red-300" data-testid="leadmagnet-error">{error}</p>}
      <button type="submit" disabled={status === "loading"} data-testid="leadmagnet-submit"
        className="md:col-span-3 inline-flex items-center justify-center gap-3 px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold border transition-colors hover:bg-[#C9A84C] hover:text-brand-ink disabled:opacity-60"
        style={{ borderColor: GOLD, color: GOLD }}>
        {status === "loading" ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
        {a.leadSubmit}
      </button>
    </form>
  );
}

export default function AnalysePrecision() {
  const { t } = useLang();
  const a = t.analyse;
  useEffect(() => {
    document.title = a.docTitle;
  }, [a.docTitle]);

  return (
    <div className="min-h-screen bg-brand-ink text-white pt-24 pb-24" data-testid="analyse-precision-page">
      <div className="max-w-[1000px] mx-auto px-6 md:px-10">

        <motion.header {...FADE_UP} className="mb-20">
          <div className="inline-flex items-center gap-2 mb-6" style={{ color: GOLD }}>
            <FileText size={18} strokeWidth={1.5} />
            <p className="text-xs uppercase tracking-[0.25em]">{a.kicker}</p>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
            {a.title}<br /><span style={{ color: GOLD }}>{a.titleAccent}</span>
          </h1>
          <p className="font-serif italic text-xl md:text-2xl text-white/80 mt-8 max-w-2xl">
            {a.sub}
          </p>
          <p className="text-white/60 text-base md:text-lg font-light mt-6 max-w-2xl leading-relaxed">
            {a.intro}
          </p>
          <div className="mt-10">
            <StripeButton label={a.cta} testId="analyse-cta-top" />
          </div>
        </motion.header>

        <Reveal>
          <section className="mb-20" data-testid="rapport-probleme">
            <SectionTitle kicker={a.k1}>{a.h1}</SectionTitle>
            <ul className="space-y-5">
              {a.problemes.map((p) => (
                <li key={p} className="flex gap-4 items-start text-white/75 leading-relaxed">
                  <span className="mt-2 shrink-0 h-1.5 w-1.5 rounded-full" style={{ backgroundColor: GOLD }} />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </section>
        </Reveal>

        <Reveal>
          <section className="mb-20 border-l-2 pl-6 md:pl-10" style={{ borderColor: GOLD }} data-testid="rapport-approche">
            <SectionTitle kicker={a.k2}>{a.h2}</SectionTitle>
            <p className="text-white/75 leading-relaxed mb-5">
              {a.approche}
            </p>
            <p className="text-white/50 text-sm leading-relaxed">
              {a.disclaimer}
            </p>
          </section>
        </Reveal>

        <Reveal>
          <section className="mb-20" data-testid="rapport-contenu">
            <SectionTitle kicker={a.k3}>{a.h3}</SectionTitle>
            <div className="grid md:grid-cols-2 gap-5">
              {a.contenu.map((c) => (
                <div key={c} className="flex gap-4 items-start border border-white/10 p-5 bg-white/[0.02]">
                  <Check size={18} className="shrink-0 mt-1" style={{ color: GOLD }} />
                  <p className="text-white/75 text-sm leading-relaxed">{c}</p>
                </div>
              ))}
            </div>
            <p className="text-white/50 text-sm mt-8">
              <span className="text-white/80">{a.formatLabel}</span> {a.format}
            </p>
          </section>
        </Reveal>

        <Reveal>
          <section className="mb-20 grid md:grid-cols-2 gap-8" data-testid="rapport-pour-qui">
            <div className="border p-8" style={{ borderColor: `${GOLD}55` }}>
              <p className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: GOLD }}>{a.forYou}</p>
              <p className="text-white/75 leading-relaxed">
                {a.forYouText}
              </p>
            </div>
            <div className="border border-white/10 p-8">
              <p className="text-xs uppercase tracking-[0.25em] mb-4 text-white/40">{a.notForYou}</p>
              <p className="text-white/55 leading-relaxed">
                {a.notForYouText}
              </p>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section className="mb-20 text-center border py-16 px-6" style={{ borderColor: GOLD, backgroundColor: "rgba(201,168,76,0.04)" }} data-testid="rapport-offre">
            <p className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: GOLD }}>{a.offerKicker}</p>
            <p className="font-serif text-6xl md:text-7xl text-white mb-4">149 €</p>
            <p className="text-white/60 max-w-xl mx-auto leading-relaxed mb-10">
              {a.offerText}
            </p>
            <StripeButton label={a.cta} testId="analyse-cta-main" />
            <p className="text-white/45 text-sm max-w-2xl mx-auto mt-10 leading-relaxed">
              {a.after}{" "}
              {a.notYet} <Link to="/diagnostic-inaugural" className="underline hover:text-white transition-colors" style={{ color: GOLD }} data-testid="analyse-di-link">{a.diLink}</Link>{a.startThere}
            </p>
          </section>
        </Reveal>

        <Reveal>
          <section className="mb-20" data-testid="rapport-leadmagnet">
            <SectionTitle kicker={a.k4}>{a.h4}</SectionTitle>
            <p className="text-white/65 leading-relaxed mb-8 max-w-2xl">
              {a.leadText}
            </p>
            <LeadMagnetForm />
          </section>
        </Reveal>

        <Reveal>
          <section className="mb-20" data-testid="rapport-faq">
            <SectionTitle kicker={a.k5}>{a.h5}</SectionTitle>
            <div className="divide-y divide-white/10 border-y border-white/10">
              {a.faq.map((f) => (
                <div key={f.q} className="py-7">
                  <h3 className="font-serif text-xl text-white mb-3">{f.q}</h3>
                  <p className="text-white/65 leading-relaxed text-sm md:text-base">
                    {f.a}
                    {f.link && (
                      <>{" "}<a href={f.link} target="_blank" rel="noopener noreferrer" className="underline hover:text-white transition-colors" style={{ color: GOLD }}>Réserver un Diagnostic Inaugural</a>.</>
                    )}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section className="mb-16 flex gap-6 items-start border border-white/10 p-8 md:p-10" data-testid="rapport-garantie">
            <ShieldCheck size={32} strokeWidth={1.2} className="shrink-0" style={{ color: GOLD }} />
            <div>
              <h2 className="font-serif text-2xl md:text-3xl text-white mb-4">{a.guaranteeTitle}</h2>
              <p className="text-white/65 leading-relaxed">
                {a.guarantee1} <a href="mailto:gestion.svh@serenite-essence.fr" className="underline" style={{ color: GOLD }}>gestion.svh@serenite-essence.fr</a> {a.guarantee2}{" "}
                <span className="text-white/90">{a.guaranteeDays}</span> {a.guarantee3}
              </p>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <div className="text-center">
            <StripeButton label={a.cta} testId="analyse-cta-bottom" />
          </div>
        </Reveal>
      </div>
    </div>
  );
}
